import { Component, h } from 'preact'
import { CategoryInfo, WidgetSetting } from './Types'
import CategoryMenu, { MenuQuicklink } from './categoryMenu'
import Icon from '../assets'
import Slider from '../slider'
import Search from '../algolia/components/Search'
import Phrase from '../../libs/translationsUtils/phrase'
import Button from '../../control-components/ui-components/Button'
import { ModulesContext } from '../../utils/globals'

interface Props {
  items: WidgetSetting[]
  groupCount: number
  showActivitySinceLastVisit: boolean
  showKnowledgeBase: boolean
  communityCategories: CategoryInfo[]
  knowledgeBaseCategories: CategoryInfo[]
  logo: string
  displaySearchInWidget: boolean
  displayFilters?: boolean
  searchInfo: {
    isUserGuest: boolean
    isFederatedSalesforceSearch: boolean
    isFederatedSkilljarSearch: boolean
    isFederatedFreshdeskSearch: boolean
    category?: number
    groupId?: number
    isParentCategory?: boolean
    searchByContentType?: string[]
  }
  permissions: {
    ideation: boolean
    productUpdates: boolean
  }
}

interface State {
  extendSearch: boolean
  isFiltersOverlay: boolean
  enableFocusToSearch: boolean
}

type linksThatPermissionsApplied = 'productUpdates' | 'ideation'

export class HeaderNavigation extends Component<Props, State> {
  permissionAppliedLinks: linksThatPermissionsApplied[] = ['productUpdates', 'ideation']
  constructor (props: Props) {
    super(props)
    this.state = {
      extendSearch: false,
      isFiltersOverlay: false,
      enableFocusToSearch: false
    }

    this.checkSliderStatus = this.checkSliderStatus.bind(this)
    this.filtersOverlayHandler = this.filtersOverlayHandler.bind(this)
    this.revertFilters = this.revertFilters.bind(this)
    this.hasPermissions = this.hasPermissions.bind(this)
  }

  checkSliderStatus (newValue: boolean) {
    const bodyWrapper = window.document.body
    if (newValue) {
      bodyWrapper.classList.add('body-overlay')
    } else {
      bodyWrapper.classList.remove('body-overlay')
    }
  }

  resetFilters (e: Event) {
    e.stopPropagation()
    e.preventDefault()

    window.dispatchEvent(new CustomEvent('resetFilters'))
  }

  revertFilters (e: Event) {
    e.stopPropagation()
    e.preventDefault()

    window.dispatchEvent(new CustomEvent('revertFilters'))
    this.filtersOverlayHandler(e, false)
  }

  filtersOverlayHandler (e: Event, value: boolean) {
    e.stopPropagation()
    e.preventDefault()

    window.dispatchEvent(new CustomEvent('filter', {
      detail: {
        display: value
      }
    }))

    if (value) {
      this.setState({
        isFiltersOverlay: value
      })
    } else {
      /*
        Items that should be in the filters are added via Portal component
        having setTimeout here is making state change async and give time to filter component to unmount
      */
      window.setTimeout(() => {
        this.setState({
          isFiltersOverlay: !this.state.isFiltersOverlay
        })
      }, 0)
    }

    const bodyEl = document.querySelector('body')

    if (bodyEl) {
      if (bodyEl.style.overflow === 'hidden') {
        bodyEl.style.overflow = 'visible'
      } else {
        bodyEl.style.overflow = 'hidden'
      }
    }
  }

  hasPermissions (item: string) {
    // @ts-expect-error
    // Item can be any string, includes here is filtering if they are not type of linksThatPermissionsApplied
    if (!this.permissionAppliedLinks.includes(item)) {
      return true
    }
    // @ts-expect-error
    // If check above makes sure type of item is linksThatPermissionsApplied
    return this.props.permissions[item]
  }

  render ({ logo, communityCategories, knowledgeBaseCategories, showActivitySinceLastVisit, showKnowledgeBase, displaySearchInWidget, searchInfo, displayFilters, items }: Props,
    { extendSearch, isFiltersOverlay, enableFocusToSearch }: State) {
    const categoryQuickLinks: Array<MenuQuicklink|null> = [{
      url: '/',
      phrase: { m: 'Common', p: 'nav.title.forum.overview' }
    }, {
      url: '/search/activity/recent',
      phrase: { m: 'Common', p: 'nav.title.forum.recent.activity' }
    }, showActivitySinceLastVisit
      ? {
          url: '/search/activity/sincelastvisit',
          phrase: { m: 'Common', p: 'nav.title.forum.activity.last.visit' }
        }
      : null, {
      url: '/search/activity/unanswered',
      phrase: { m: 'Common', p: 'nav.title.forum.unanswered.questions' }
    }]
    const knowledgeBaseQuickLinks: Array<MenuQuicklink|null> = [showKnowledgeBase
      ? {
          url: '/knowledge-base',
          phrase: { m: 'Forum', p: 'nav.title.knowledgebase' }
        }
      : null]
    return (
      <div className={`header-navigation-items_and_search ${extendSearch ? 'search-extended' : ''}`}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <nav role='navigation'>
            <ul className='header-navigation-items_menu'>
              {
                items.filter(item => item.visibility)
                  .filter(item => this.hasPermissions(item.key))
                  .map((item) => {
                    if (item.key === 'community') {
                      if (!communityCategories.length) {
                        return null
                      }
                      return (
                        <CategoryMenu
                          name={item.name}
                          popupName='community-categories'
                          categories={communityCategories}
                          quickLinks={categoryQuickLinks}
                        />
                      )
                    } else if (item.key === 'knowledgeBase') {
                      if (!knowledgeBaseCategories.length) {
                        return null
                      }
                      return (
                        <CategoryMenu
                          popupName='knowledgebase-categories'
                          name={item.name}
                          categories={knowledgeBaseCategories}
                          quickLinks={knowledgeBaseQuickLinks}
                        />
                      )
                    } else {
                      return (
                        <li className='header-navigation_list-item'>
                          <a href={item.url} className='header-navigation_link' target={item.external ? '_blank' : ''} rel='noreferrer'> {item.name} </a>
                        </li>
                      )
                    }
                  })
                }
            </ul>
          </nav>
          <div className='header-navigation-items_hamburger'>
            <Slider
              title='Menu'
              onCallback={(newValue) => this.checkSliderStatus(newValue)}
              trigger={<Icon icon='menu' />}
            >
              <ul className='header-navigation-items_hamburger-list'>
                {
                  items.filter(item => item.visibility).map((item) => {
                    if (item.key === 'community') {
                      if (!communityCategories.length) {
                        return null
                      }
                      return (
                        <CategoryMenu
                          popupName='community-categories'
                          name={item.name}
                          categories={communityCategories}
                          quickLinks={categoryQuickLinks}
                        />
                      )
                    } else if (item.key === 'knowledgeBase') {
                      if (!knowledgeBaseCategories.length) {
                        return null
                      }
                      return (
                        <CategoryMenu
                          popupName='knowledgebase-categories'
                          name={item.name}
                          categories={knowledgeBaseCategories}
                          quickLinks={knowledgeBaseQuickLinks}
                        />
                      )
                    } else {
                      return (
                        <li className='header-navigation_list-item'>
                          <a href={item.url} target={item.external ? '_blank' : ''} rel='noreferrer' className={`header-navigation_link title-${item.name.split(' ').join('-').toLowerCase()}`}> {item.name} </a>
                        </li>
                      )
                    }
                  })
                  }
              </ul>
            </Slider>
          </div>
          {
            logo && (
              <div className='header-navigation_logo-wrapper is-hidden-L'>
                <a href='/' target='_self' className='header-navigation_logo-anchor'>
                  <img className='header-navigation_logo' src={logo} title='' alt='Logo' />
                </a>
              </div>
            )
            }
        </div>
        {displaySearchInWidget
          ? <div className={`header-navigation_extendable-search ${extendSearch ? 'extended' : ''}`}>
            <div className={`header-navigation_extended-search ${!extendSearch ? 'is-hidden' : ''}`}>
              <form>
                <Search
                  {...searchInfo}
                  enableFocusToSearch={enableFocusToSearch}
                  callbackOnOutsideClick={() => this.setState({ extendSearch: false, enableFocusToSearch: false })}
                />
              </form>
            </div>
            <button
              type='button'
              className={`btn btn--secondary search-icon header-navigation_extendable-search-icon ${extendSearch ? 'is-hidden' : ''}`}
              onClick={() => this.setState({ extendSearch: true, enableFocusToSearch: true })}
            >
              <Icon icon='search' width={16} height={16} />
            </button>
            {displayFilters
              ? <button
                  className='instant-search__filter is-hidden-S'
                  onClick={(e: Event) => this.filtersOverlayHandler(e, true)}
                >
                <Icon width={18} height={12} color='#6B707E' icon='filter' />
              </button>

              : null}
            {isFiltersOverlay
              ? <div className={`instant-search__results-wrapper is-hidden-L ${!isFiltersOverlay ? '' : ''}`}>
                <div className={`instant-search__results-wrapper-filter ${!isFiltersOverlay ? 'instant-search__results-wrapper-filter--hide' : ''}`}>
                  <div className='instant-search__results-wrapper-filter-header'>
                    <Icon icon='close' onClick={(e) => this.revertFilters(e)} />
                    <h3
                      className='instant-search__results-wrapper-filter-header-heading'
                    >
                      <Phrase p='advanced.search.filters' />
                    </h3>
                    <button
                      onClick={(e) => this.resetFilters(e)}
                      className='instant-search__results-wrapper-filter-header-btn'
                    >
                      <Phrase m='Common' p='advanced.search.filter.clear.all' />
                    </button>
                  </div>
                  {
/*
* Filters can be added via Portal component from real place of filters.
* Since Search does not include them it does not care about which items needs to be display in which order,
* all those decisions can be done on filter component
                * */
                }
                  <div className='instant-search__results-wrapper-filters' />
                  <div
                    className='instant-search__results-wrapper-filter-show-more-wrapper'
                  >
                    <Button
                      cta
                      class='instant-search__results-wrapper-filter-show-more'
                      onClick={(e: Event) => this.filtersOverlayHandler(e, false)}
                    >
                      <Phrase p='show.results' />
                    </Button>
                  </div>
                </div>
              </div>
              : null}
          </div>
          : null}
      </div>
    )
  }
}

const HeaderNavigationWithModules = (props: Props) => {
  const getItems = (props: Props, modules: string[]) => {
    if (props.items?.length) return props.items

    let defaultItems: WidgetSetting[] = [{
      key: 'community',
      visibility: true,
      name: 'Community'
    }]

    if (modules.includes('knowledgeBase')) {
      defaultItems = defaultItems.concat({
        key: 'knowledgeBase',
        visibility: true,
        name: 'Knowledge Base'
      })
    }

    if (modules.includes('ideationV2')) {
      defaultItems = defaultItems.concat({
        key: 'ideation',
        visibility: true,
        name: 'Ideas',
        url: '/ideas'
      })
    }

    if (modules.includes('productUpdates')) {
      defaultItems = defaultItems.concat({
        key: 'productUpdates',
        visibility: true,
        name: 'Product Updates',
        url: '/product-updates'
      })
    }

    if (modules.includes('event')) {
      defaultItems = defaultItems.concat({
        key: 'event',
        visibility: true,
        name: 'Events',
        url: '/events'
      })
    }

    if (modules.includes('group') && props.groupCount) {
      defaultItems = defaultItems.concat({
        key: 'group',
        visibility: true,
        name: 'Groups',
        url: '/groups'
      })
    }

    return defaultItems
  }

  return (
    <ModulesContext.Consumer>
      {(modules) => {
        const items = getItems(props, modules)
        return <HeaderNavigation {...props} items={items} showKnowledgeBase={modules.includes('knowledgeBaseOverviewOpenForAllUsers')} />
      }}
    </ModulesContext.Consumer>
  )
}

export default HeaderNavigationWithModules
